import React from 'react';
import { IonToggle } from '@ionic/react';

/**
 * AuthCard — one auth method (Basic / IP / Bearer) rendered in the shared
 * `addon-card` shape (the same card the Billing page + pipeline webhook-auth
 * use). Title left, an IonToggle top-right that IS the on/off state; when
 * enabled the card expands to show its config (passed as `children`).
 *
 * Presentational only — the container owns the draft state, save handlers and
 * the API shape. Reused by the pipeline webhook-auth section and the managed-
 * app access-control section.
 *
 * The `pipeline-auth-*` class names are intentional: those rules live in
 * PipelineDetailPage.css, which BOTH the pipeline and app detail pages import,
 * so reusing them keeps one source of truth for the look with zero CSS churn.
 *
 * Keep this module-level (never define it inside a container component): a new
 * function identity each render would unmount + remount every card on any
 * state change — a visible flash.
 */
const AuthCard = ({ name, description, enabled, onToggle, toggling, children, hint }) => (
  <div className={`addon-card pipeline-auth-card ${enabled ? 'addon-card--enabled' : ''}`}>
    <div className="addon-card-header">
      <div className="addon-card-header-title">
        <h3 className="addon-card-name">{name}</h3>
        {/* Optional CSS-only hover tooltip — reuses the global `pipeline-help-icon`
            ::after rule (PipelineDetailPage.css). Used to explain how the auth
            layers combine (e.g. Basic/Bearer are OR, IP is a separate gate). */}
        {hint && (
          <span
            className="pipeline-help-icon"
            data-hint={hint}
            tabIndex={0}
            role="tooltip"
            aria-label={hint}
          >
            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.3" />
              <text x="8" y="11.5" textAnchor="middle" fontSize="9" fontWeight="600" fill="currentColor">?</text>
            </svg>
          </span>
        )}
      </div>
      <div className="addon-card-header-action">
        {/* The switch IS the on/off state — no separate Enabled badge (the
            toggle position already says it). Optimistic flip lives in the
            onToggle handler; `disabled` blocks double-clicks mid-PATCH. */}
        <IonToggle
          className="pipeline-auth-switch"
          checked={enabled}
          disabled={toggling}
          onIonChange={(e) => {
            if (e.detail.checked !== enabled) onToggle(e.detail.checked);
          }}
          aria-label={enabled ? `Disable ${name}` : `Enable ${name}`}
        />
      </div>
    </div>
    {description && <p className="addon-card-desc">{description}</p>}
    {enabled && children && (
      <div className="addon-card-children pipeline-auth-block-body">{children}</div>
    )}
  </div>
);

export default AuthCard;
